import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLFormBasics = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Forms & Input</div>
                <h1>Form Basics & Input Types</h1>
                <p className="hero-lead">
                    Master HTML forms to collect user input. Learn form structure, input types,
                    validation, accessibility, and how forms submit data to servers.
                </p>
            </div>

            <div className="content-section">
                <h2>The &lt;form&gt; Element</h2>
                <p>
                    Forms collect user input and send it to a server for processing. They're essential
                    for logins, signups, search, checkouts, and any user interaction.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Form Structure</span>
                    </div>
                    <CodeBlock language="html" code={`<form action="/submit" method="POST">
    <label for="username">Username:</label>
    <input type="text" id="username" name="username" required>
    
    <label for="password">Password:</label>
    <input type="password" id="password" name="password" required>
    
    <button type="submit">Login</button>
</form>`} />
                </div>

                <div className="form-anatomy">
                    <h3>Form Attributes:</h3>
                    <ul>
                        <li><code>action</code> - URL where form data is sent</li>
                        <li><code>method</code> - HTTP method (GET or POST)</li>
                        <li><code>name</code> - Form identifier</li>
                        <li><code>autocomplete</code> - Enable/disable browser autocomplete</li>
                        <li><code>novalidate</code> - Disable HTML5 validation</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Form Methods: GET vs POST</h2>

                <div className="comparison-grid">
                    <div className="comparison-card">
                        <h3>GET Method</h3>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<form action="/search" method="GET">
    <input type="search" name="q">
    <button>Search</button>
</form>

<!-- Submits to: /search?q=html --&gt;`} />
                        </div>
                        <h4>Characteristics:</h4>
                        <ul>
                            <li>Data in URL query string</li>
                            <li>Visible in browser history</li>
                            <li>Can be bookmarked</li>
                            <li>Limited data size (~2000 chars)</li>
                            <li>Should not modify data</li>
                        </ul>
                        <h4>Use For:</h4>
                        <ul>
                            <li>Search forms</li>
                            <li>Filters</li>
                            <li>Pagination</li>
                            <li>Retrieving data</li>
                        </ul>
                    </div>

                    <div className="comparison-card">
                        <h3>POST Method</h3>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<form action="/login" method="POST">
    <input type="text" name="username">
    <input type="password" name="password">
    <button>Login</button>
</form>

<!-- Data sent in request body (hidden) --&gt;`} />
                        </div>
                        <h4>Characteristics:</h4>
                        <ul>
                            <li>Data in request body</li>
                            <li>Not visible in URL</li>
                            <li>Cannot be bookmarked</li>
                            <li>No size limit</li>
                            <li>Can modify server data</li>
                        </ul>
                        <h4>Use For:</h4>
                        <ul>
                            <li>Login forms</li>
                            <li>Registration</li>
                            <li>File uploads</li>
                            <li>Creating/updating data</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Text Input Types</h2>

                <h3>1. Text - <code>type="text"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="fullname">Full Name:</label>
<input type="text" id="fullname" name="fullname" placeholder="John Doe" maxlength="50">`} />
                </div>

                <h3>2. Password - <code>type="password"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="pwd">Password:</label>
<input type="password" id="pwd" name="password" required minlength="8">`} />
                </div>

                <h3>3. Email - <code>type="email"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="email">Email:</label>
<input type="email" id="email" name="email" required placeholder="user@example.com">`} />
                </div>
                <p>Validates email format and shows email keyboard on mobile.</p>

                <h3>4. Tel - <code>type="tel"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="phone">Phone:</label>
<input type="tel" id="phone" name="phone" pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}">`} />
                </div>
                <p>Shows numeric keyboard on mobile devices.</p>

                <h3>5. URL - <code>type="url"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="website">Website:</label>
<input type="url" id="website" name="website" placeholder="https://example.com">`} />
                </div>

                <h3>6. Search - <code>type="search"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="search">Search:</label>
<input type="search" id="search" name="q" placeholder="Search...">`} />
                </div>
                <p>Shows clear button (X) when text is entered.</p>
            </div>

            <div className="content-section">
                <h2>Number & Range Inputs</h2>

                <h3>1. Number - <code>type="number"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="age">Age:</label>
<input type="number" id="age" name="age" min="0" max="120" step="1">`} />
                </div>
                <p>Spinners for increment/decrement, numeric keyboard on mobile.</p>

                <h3>2. Range - <code>type="range"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="volume">Volume:</label>
<input type="range" id="volume" name="volume" min="0" max="100" value="50">
<output for="volume">50</output>

<script>
const range = document.getElementById('volume');
const output = document.querySelector('output');
range.addEventListener('input', () => {
    output.value = range.value;
});
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Date & Time Inputs</h2>

                <h3>1. Date - <code>type="date"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="birthday">Birthday:</label>
<input type="date" id="birthday" name="birthday" min="1900-01-01" max="2024-12-31">`} />
                </div>

                <h3>2. Time - <code>type="time"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="meeting">Meeting Time:</label>
<input type="time" id="meeting" name="meeting" min="09:00" max="18:00">`} />
                </div>

                <h3>3. Datetime-local - <code>type="datetime-local"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="appointment">Appointment:</label>
<input type="datetime-local" id="appointment" name="appointment">`} />
                </div>

                <h3>4. Month - <code>type="month"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="expiry">Card Expiry:</label>
<input type="month" id="expiry" name="expiry" min="2024-01">`} />
                </div>

                <h3>5. Week - <code>type="week"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="week">Select Week:</label>
<input type="week" id="week" name="week">`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Selection Inputs</h2>

                <h3>1. Checkbox - <code>type="checkbox"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Single checkbox --&gt;
<input type="checkbox" id="terms" name="terms" required>
<label for="terms">I agree to terms and conditions</label>

<!-- Multiple checkboxes --&gt;
<fieldset>
    <legend>Interests:</legend>
    <input type="checkbox" id="html" name="interests" value="html">
    <label for="html">HTML</label>
    
    <input type="checkbox" id="css" name="interests" value="css">
    <label for="css">CSS</label>
    
    <input type="checkbox" id="js" name="interests" value="javascript">
    <label for="js">JavaScript</label>
</fieldset>`} />
                </div>

                <h3>2. Radio - <code>type="radio"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<fieldset>
    <legend>Choose a payment method:</legend>
    
    <input type="radio" id="credit" name="payment" value="credit" checked>
    <label for="credit">Credit Card</label>
    
    <input type="radio" id="debit" name="payment" value="debit">
    <label for="debit">Debit Card</label>
    
    <input type="radio" id="paypal" name="payment" value="paypal">
    <label for="paypal">PayPal</label>
</fieldset>`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Radio vs Checkbox:</strong> Radio buttons allow one selection from a group
                        (same name attribute). Checkboxes allow multiple selections.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>File & Color Inputs</h2>

                <h3>1. File - <code>type="file"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Single file --&gt;
<label for="avatar">Profile Picture:</label>
<input type="file" id="avatar" name="avatar" accept="image/*">

<!-- Multiple files --&gt;
<label for="photos">Upload Photos:</label>
<input type="file" id="photos" name="photos" multiple accept="image/png, image/jpeg">

<!-- Specific file types --&gt;
<label for="document">Upload PDF:</label>
<input type="file" id="document" name="document" accept=".pdf,application/pdf">`} />
                </div>

                <h3>2. Color - <code>type="color"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="favcolor">Favorite Color:</label>
<input type="color" id="favcolor" name="favcolor" value="#ff0000">`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Hidden & Submit Inputs</h2>

                <h3>1. Hidden - <code>type="hidden"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Store data not shown to user --&gt;
<input type="hidden" name="user_id" value="12345">
<input type="hidden" name="csrf_token" value="abc123def456">`} />
                </div>

                <h3>2. Submit - <code>type="submit"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<input type="submit" value="Submit Form">

<!-- Better: use <button> --&gt;
<button type="submit">Submit Form</button>`} />
                </div>

                <h3>3. Button - <code>type="button"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<button type="button" onclick="doSomething()">Click Me</button>`} />
                </div>

                <h3>4. Reset - <code>type="reset"</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<button type="reset">Clear Form</button>`} />
                </div>

                <Warning><strong> Avoid Reset Buttons:</strong> Users rarely want to clear entire forms.
                    Reset buttons often cause frustration when clicked accidentally.</Warning>
            </div>

            <div className="content-section">
                <h2>Common Input Attributes</h2>

                <div className="attributes-grid">
                    <div className="attribute-card">
                        <h3><code>required</code></h3>
                        <p>Field must be filled before submission</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="text" name="username" required>`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>placeholder</code></h3>
                        <p>Hint text shown when empty</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="email" placeholder="user@example.com">`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>disabled</code></h3>
                        <p>Make input non-editable and unsubmittable</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="text" value="Cannot edit" disabled>`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>readonly</code></h3>
                        <p>Editable = false, but submittable</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="text" value="Read only" readonly>`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>maxlength</code></h3>
                        <p>Maximum character limit</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="text" maxlength="50">`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>minlength</code></h3>
                        <p>Minimum character requirement</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="password" minlength="8">`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>pattern</code></h3>
                        <p>Regular expression validation</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="text" pattern="[A-Za-z]{3,}">`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>autocomplete</code></h3>
                        <p>Control browser autocomplete</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="email" autocomplete="email">
<input type="text" autocomplete="off">`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Form Example</h2>
                <div className="code-example">
                    <div className="code-header">
                        <span>Registration Form</span>
                    </div>
                    <CodeBlock language="html" code={`<form action="/register" method="POST">
    <h2>Create Account</h2>
    
    <!-- Text inputs --&gt;
    <label for="name">Full Name:</label>
    <input type="text" id="name" name="name" required>
    
    <label for="email">Email:</label>
    <input type="email" id="email" name="email" required>
    
    <label for="password">Password:</label>
    <input type="password" id="password" name="password" 
           required minlength="8" pattern="(?=.*\\d)(?=.*[a-z])(?=.*[A-Z]).{8,}">
    <small>Must contain uppercase, lowercase, and number</small>
    
    <!-- Date --&gt;
    <label for="dob">Date of Birth:</label>
    <input type="date" id="dob" name="dob" required>
    
    <!-- Radio --&gt;
    <fieldset>
        <legend>Gender:</legend>
        <input type="radio" id="male" name="gender" value="male">
        <label for="male">Male</label>
        
        <input type="radio" id="female" name="gender" value="female">
        <label for="female">Female</label>
        
        <input type="radio" id="other" name="gender" value="other">
        <label for="other">Other</label>
    </fieldset>
    
    <!-- Checkbox --&gt;
    <input type="checkbox" id="newsletter" name="newsletter" value="yes">
    <label for="newsletter">Subscribe to newsletter</label>
    
    <input type="checkbox" id="terms" name="terms" required>
    <label for="terms">I agree to Terms & Conditions</label>
    
    <!-- Buttons --&gt;
    <button type="submit">Create Account</button>
    <button type="button" onclick="window.location='/login'">
        Already have an account?
    </button>
</form>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use appropriate input types</li>
                            <li>Always include labels</li>
                            <li>Use GET for searches, POST for data changes</li>
                            <li>Add required and validation attributes</li>
                            <li>Provide helpful placeholder text</li>
                            <li>Use fieldset for grouped inputs</li>
                            <li>Test on mobile devices</li>
                            <li>Provide clear error messages</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use placeholder as label</li>
                            <li>Forget name attributes</li>
                            <li>Use type="text" for everything</li>
                            <li>Add reset buttons unnecessarily</li>
                            <li>Make fields required without indication</li>
                            <li>Use GET for sensitive data</li>
                            <li>Forget mobile keyboard optimization</li>
                            <li>Disable paste in password fields</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLFormBasics;



